import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    Grid,
    TextField,
    Stack,
    MenuItem,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Divider
} from '@mui/material';
import { ArrowBack, CloudUpload, CheckCircle, InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubmitVendorKyc, useVendorKyc } from 'hooks/useKyc';
import vendorKycService from 'api/vendorKycService';
import statesAndLgas from 'utils/statesAndLgas';

const steps = ['Identity Verification', 'Proof of Address', 'Next of Kin', 'Business Documents'];

const KycSubmission = () => {
    const navigate = useNavigate();
    const { data: existingKyc, isLoading: loadingExisting } = useVendorKyc();
    const submitKyc = useSubmitVendorKyc();

    const [activeStep, setActiveStep] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        identity: { documentType: 'NIN', documentNumber: '', documentUrl: '' },
        nin: { nin: '' },
        address: { street: '', city: '', state: '', documentUrl: '' },
        nextOfKin: { name: '', relationship: '', phone: '', address: '' },
        businessDocs: { name: '', phone: '', address: '', documentUrl: '' }
    });

    useEffect(() => {
        if (existingKyc) {
            const idDoc = existingKyc.identity || {};
            setFormData({
                identity: {
                    documentType: 'NIN',
                    documentNumber: idDoc.identityNumber || idDoc.documentNumber || '',
                    documentUrl: idDoc.identityDocument || idDoc.documentUrl || ''
                },
                nin: { nin: existingKyc.nin?.nin || idDoc.identityNumber || idDoc.documentNumber || '' },
                address: existingKyc.address || { street: '', city: '', state: '', documentUrl: '' },
                nextOfKin: existingKyc.nextOfKin || { name: '', relationship: '', phone: '', address: '' },
                businessDocs: existingKyc.guarantor || { name: '', phone: '', address: '', documentUrl: '' }
            });
        }
    }, [existingKyc]);

    const handleFileUpload = async (e, section, field = 'documentUrl') => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setError('');
        try {
            const res = await vendorKycService.uploadFile(file);
            // res is response.data from axiosClient
            const uploadedUrl = res.data?.url || res.url;

            if (!uploadedUrl) {
                throw new Error('No URL returned from server');
            }

            setFormData((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: uploadedUrl }
            }));
        } catch (err) {
            setError('Failed to upload file. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const handleSubmit = () => {
        submitKyc.mutate(formData, {
            onSuccess: () => navigate('/vendor/profile'),
            onError: (err) => setError(err.response?.data?.message || 'Submission failed')
        });
    };

    if (loadingExisting)
        return (
            <Box p={10} textAlign="center">
                <CircularProgress />
            </Box>
        );

    const isVerified = existingKyc?.status === 'verified' || existingKyc?.status === 'approved';

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Stack spacing={3}>
                        <Typography variant="h6" fontWeight={700}>
                            Identity Verification (NIN)
                        </Typography>
                        <Alert severity="info" icon={<InfoOutlined />}>
                            Please provide your National Identification Number (NIN) and a clear photo or scan of your NIN slip or card.
                        </Alert>
                        <TextField
                            fullWidth
                            label="National Identification Number (NIN)"
                            placeholder="Enter 11-digit NIN"
                            value={formData.nin.nin}
                            disabled={isVerified}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                                setFormData({
                                    ...formData,
                                    nin: { nin: val },
                                    identity: { ...formData.identity, documentType: 'NIN', documentNumber: val }
                                });
                            }}
                        />
                        <Box sx={{ border: '2px dashed #eee', p: 3, textAlign: 'center', borderRadius: 2 }}>
                            {formData.identity.documentUrl ? (
                                <Stack alignItems="center">
                                    <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                        NIN Document Uploaded
                                    </Typography>
                                    {!isVerified && (
                                        <Button size="small" component="label" sx={{ mt: 1 }}>
                                            Change File
                                            <input type="file" hidden onChange={(e) => handleFileUpload(e, 'identity')} />
                                        </Button>
                                    )}
                                </Stack>
                            ) : (
                                <Stack spacing={1} alignItems="center">
                                    <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                    <Typography variant="body2">Upload NIN Slip / Card (PDF or Image)</Typography>
                                    <Button variant="outlined" component="label" sx={{ mt: 1 }} disabled={isVerified}>
                                        Select File
                                        <input type="file" hidden onChange={(e) => handleFileUpload(e, 'identity')} />
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={3}>
                        <Typography variant="h6" fontWeight={700}>
                            Proof of Address
                        </Typography>
                        <TextField
                            fullWidth
                            label="Street Address"
                            placeholder="e.g. 123 Business Way"
                            value={formData.address.street}
                            disabled={isVerified}
                            onChange={(e) => setFormData({ ...formData, address: { ...formData.address, street: e.target.value } })}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="State"
                                    value={formData.address.state}
                                    disabled={isVerified}
                                    onChange={(e) => {
                                        const state = e.target.value;
                                        setFormData({
                                            ...formData,
                                            address: {
                                                ...formData.address,
                                                state,
                                                city: '' // Reset city when state changes
                                            }
                                        });
                                    }}
                                >
                                    {Object.keys(statesAndLgas)
                                        .sort()
                                        .map((state) => (
                                            <MenuItem key={state} value={state}>
                                                {state}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="LGA (City)"
                                    disabled={!formData.address.state || isVerified}
                                    value={formData.address.city}
                                    onChange={(e) => setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })}
                                >
                                    {(statesAndLgas[formData.address.state] || []).map((lga) => (
                                        <MenuItem key={lga} value={lga}>
                                            {lga}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                        <Box sx={{ border: '2px dashed #eee', p: 3, textAlign: 'center', borderRadius: 2 }}>
                            {formData.address.documentUrl ? (
                                <Stack alignItems="center">
                                    <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                        Utility Bill Uploaded
                                    </Typography>
                                    {!isVerified && (
                                        <Button size="small" component="label" sx={{ mt: 1 }}>
                                            Change File
                                            <input type="file" hidden onChange={(e) => handleFileUpload(e, 'address')} />
                                        </Button>
                                    )}
                                </Stack>
                            ) : (
                                <Stack spacing={1} alignItems="center">
                                    <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                    <Typography variant="body2">Upload Utility Bill / Proof of Address</Typography>
                                    <Button variant="outlined" component="label" sx={{ mt: 1 }} disabled={isVerified}>
                                        Select File
                                        <input type="file" hidden onChange={(e) => handleFileUpload(e, 'address')} />
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={3}>
                        <Typography variant="h6" fontWeight={700}>
                            Next of Kin Details
                        </Typography>
                        <TextField
                            fullWidth
                            label="Full Name"
                            value={formData.nextOfKin.name}
                            disabled={isVerified}
                            onChange={(e) => setFormData({ ...formData, nextOfKin: { ...formData.nextOfKin, name: e.target.value } })}
                        />
                        <TextField
                            fullWidth
                            label="Relationship"
                            value={formData.nextOfKin.relationship}
                            disabled={isVerified}
                            onChange={(e) =>
                                setFormData({ ...formData, nextOfKin: { ...formData.nextOfKin, relationship: e.target.value } })
                            }
                        />
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.nextOfKin.phone}
                            disabled={isVerified}
                            onChange={(e) => setFormData({ ...formData, nextOfKin: { ...formData.nextOfKin, phone: e.target.value } })}
                        />
                        <TextField
                            fullWidth
                            label="Residential Address"
                            multiline
                            rows={2}
                            value={formData.nextOfKin.address}
                            disabled={isVerified}
                            onChange={(e) => setFormData({ ...formData, nextOfKin: { ...formData.nextOfKin, address: e.target.value } })}
                        />
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={3}>
                        <Typography variant="h6" fontWeight={700}>
                            Business Reference / Guarantor
                        </Typography>
                        <Alert severity="info" icon={<InfoOutlined />}>
                            Provide a reference who can vouch for your business.
                        </Alert>
                        <TextField
                            fullWidth
                            label="Reference Name"
                            value={formData.businessDocs.name}
                            disabled={isVerified}
                            onChange={(e) => setFormData({ ...formData, businessDocs: { ...formData.businessDocs, name: e.target.value } })}
                        />
                        <TextField
                            fullWidth
                            label="Reference Phone"
                            value={formData.businessDocs.phone}
                            disabled={isVerified}
                            onChange={(e) =>
                                setFormData({ ...formData, businessDocs: { ...formData.businessDocs, phone: e.target.value } })
                            }
                        />
                        <TextField
                            fullWidth
                            label="Business Address"
                            multiline
                            rows={2}
                            value={formData.businessDocs.address}
                            disabled={isVerified}
                            onChange={(e) =>
                                setFormData({ ...formData, businessDocs: { ...formData.businessDocs, address: e.target.value } })
                            }
                        />
                        <Box sx={{ border: '2px dashed #eee', p: 3, textAlign: 'center', borderRadius: 2 }}>
                            {formData.businessDocs.documentUrl ? (
                                <Stack alignItems="center">
                                    <Typography variant="body2" color="success.main" mb={1}>
                                        Business Doc Uploaded
                                    </Typography>
                                    {!isVerified && (
                                        <Button size="small" component="label">
                                            Change File
                                            <input type="file" hidden onChange={(e) => handleFileUpload(e, 'businessDocs')} />
                                        </Button>
                                    )}
                                </Stack>
                            ) : (
                                <Button component="label" startIcon={<CloudUpload />} disabled={isVerified}>
                                    Upload CAC or Business Permit (Optional)
                                    <input type="file" hidden onChange={(e) => handleFileUpload(e, 'businessDocs')} />
                                </Button>
                            )}
                        </Box>
                    </Stack>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <Box p={{ xs: 2, md: 4 }} maxWidth="md" margin="auto">
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid #eee' }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={4}>
                    <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f5f5f5' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="h4" fontWeight={900}>
                                KYC Verification
                            </Typography>
                            {isVerified && <Chip label="VERIFIED" color="success" size="small" sx={{ fontWeight: 800 }} />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {isVerified
                                ? 'Your records have been verified and approved.'
                                : 'Complete your profile to unlock all platform features.'}
                        </Typography>
                    </Box>
                </Stack>

                {isVerified && (
                    <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 4, borderRadius: 2 }}>
                        Your KYC verification is complete. These records are now locked and cannot be edited.
                    </Alert>
                )}

                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {uploading && (
                    <Box mb={2}>
                        <CircularProgress size={20} sx={{ mr: 1 }} /> Uploading document...
                    </Box>
                )}

                <Box sx={{ minHeight: 400 }}>{renderStepContent(activeStep)}</Box>

                <Divider sx={{ my: 4 }} />

                <Stack direction="row" justifyContent="space-between">
                    <Button disabled={activeStep === 0} onClick={handleBack} sx={{ px: 4 }}>
                        Back
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={submitKyc.isPending || uploading || isVerified}
                            sx={{ px: 6, py: 1.5, borderRadius: 2, fontWeight: 700 }}
                        >
                            {submitKyc.isPending ? 'Submitting...' : isVerified ? 'Verified' : 'Submit Records'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={uploading}
                            sx={{ px: 6, py: 1.5, borderRadius: 2, fontWeight: 700 }}
                        >
                            Continue
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default KycSubmission;
